import {
    OneOfCustomPropertyDtos,
    OneOfCustomPropertyOverrideDtos
} from '@domain/api/design-api.interop';
import {
    FeedDto,
    WidgetElementDto,
    WidgetElementOverrideDto,
    WidgetSelectionOptionDto
} from '@domain/api/generated/design-api';
import { WidgetFontStyleDto } from '@domain/api/generated/sapi';
import { IColor } from '@domain/color';
import { IWidgetText } from '@domain/creativeset/version';
import { IFontStyle } from '@domain/font';
import { OneOfCustomPropertyDto } from '@domain/serialization';
import {
    IWidgetCustomProperty,
    IWidgetElementProperty,
    IWidgetImage,
    IWidgetSelectOption,
    OneOfCustomPropertyValue,
    WidgetUnits
} from '@domain/widget';
import { isUrl } from '@studio/utils/url';
import { clamp } from '@studio/utils/utils';
import { parseColor, toRGBA } from '../color.utils';
import { createFeed, isFeedValue } from '../elements/feed/feeds.utils';
import {
    deserializeFeedString,
    serializeElementPropertyToStringValue
} from './property-value-serializer';
import { deserializeWidgetText } from './text-serializer';
import { serializeWidgetTextValue } from './versions/widgetText-serializer';

/** Deserialize widget element properties. Mainly needed for Brandlibrary elements */
export function deserializeWidgetPropertyValue(
    property: IWidgetElementProperty
): OneOfCustomPropertyValue {
    const { unit, value } = property;

    switch (unit) {
        case 'text':
            return deserializeWidgetText(value as string);
        case 'number': {
            return clamp(Number(value), -9999999, 9999999);
        }
        case 'boolean': {
            if (typeof value === 'string') {
                return value.toLowerCase() === 'true';
            }
            if (typeof value === 'boolean') {
                return value as boolean;
            }

            throw new Error(`Could not deserialize property with unit ${unit}`);
        }
        case 'select':
            return typeof window !== 'undefined'
                ? JSON.parse(atob(value as string))
                : JSON.parse(Buffer.from(value as string, 'base64').toString());
        case 'color': {
            if (typeof value === 'string') {
                return parseColor(value);
            }

            throw new Error(`Could not deserialize property with unit ${unit}`);
        }
        case 'image': {
            try {
                const imageValue: unknown = JSON.parse(value as string);

                if (typeof imageValue === 'string') {
                    const splits = imageValue.split(' ');
                    return { id: splits[0] ?? '', src: splits[1] ?? '' } satisfies IWidgetImage;
                }

                if (
                    imageValue &&
                    typeof imageValue === 'object' &&
                    'id' in imageValue &&
                    typeof imageValue.id === 'string'
                ) {
                    // Bad data :(
                    if ('url' in imageValue && typeof imageValue.url === 'string') {
                        return { id: imageValue.id, src: imageValue.url } satisfies IWidgetImage;
                    }

                    // Good data
                    if ('src' in imageValue && typeof imageValue.src === 'string') {
                        return { id: imageValue.id, src: imageValue.src } satisfies IWidgetImage;
                    }
                }
            } catch {
                // Old data
                const splits = (value as string).split(' ');
                return { id: splits[0], src: splits[1] } satisfies IWidgetImage;
            }

            throw new Error('Could not deserialize widget custom image property');
        }
        case 'font': {
            const splits = (value as string).split(' '); // TODO: clean this up. It shouldn't be a string

            let src = splits[3];

            try {
                // Support old data
                if (!isUrl(src)) {
                    if (typeof window !== 'undefined') {
                        src = atob(splits[3]);
                    } else {
                        src = Buffer.from(splits[3], 'base64').toString();
                    }
                }
            } catch {
                /* empty */
            }

            const style = (splits[2] as WidgetFontStyleDto['style']) ?? 'normal';
            return {
                id: splits[0],
                weight: parseInt(splits[1], 10) as 100,
                style,
                src,
                fontFamilyId: splits[4]
            } satisfies IFontStyle;
        }
        case 'feed': {
            return deserializeFeedString(value);
        }
    }
}

export function serializeWidgetPropertyValue(
    unit: WidgetUnits,
    value?: OneOfCustomPropertyValue
): string | undefined {
    if (value === undefined) {
        return undefined;
    }

    switch (unit) {
        case 'number':
        case 'boolean':
        case 'color':
            return value.toString();

        case 'text':
            return serializeWidgetTextValue(value as IWidgetText);

        case 'font':
            return serializeElementPropertyToStringValue('font', value as IFontStyle);

        case 'feed':
        case 'image':
            return JSON.stringify(value);

        case 'select': {
            const trimmedValue = (value as IWidgetSelectOption[]).map(
                (val): IWidgetSelectOption => ({
                    ...val,
                    value: val.value.trim()
                })
            );

            if (typeof window !== 'undefined') {
                return btoa(JSON.stringify(trimmedValue));
            } else {
                return Buffer.from(trimmedValue as any).toString('base64');
            }
        }
    }
}

export function convertCustomPropertyToDto(property: IWidgetCustomProperty): OneOfCustomPropertyDto {
    let value = property.versionPropertyId ? undefined : property.value;

    if (property.unit === 'color') {
        value = toRGBA(property.value as IColor);
    }

    return {
        label: property.label,
        name: property.name,
        unit: property.unit,
        value,
        versionPropertyId: property.versionPropertyId
    } as OneOfCustomPropertyDto;
}

export function convertCustomPropertiesToDapiDtoOverride(
    properties: WidgetElementDto['customProperties']
): WidgetElementOverrideDto['customProperties'] {
    const customProperties: WidgetElementOverrideDto['customProperties'] = {};

    for (const property of properties) {
        customProperties[property.name] = convertCustomPropertyDapiDtoToOverride(property);
    }

    return customProperties;
}

function convertCustomPropertyDapiDtoToOverride(
    property: OneOfCustomPropertyDtos
): OneOfCustomPropertyOverrideDtos {
    switch (property.$type) {
        case 'CustomPropertyBooleanDto':
            return {
                $type: 'CustomPropertyBooleanOverrideDto',
                value: property.value as boolean
            };
        case 'CustomPropertyColorDto':
            return {
                $type: 'CustomPropertyColorOverrideDto',
                value: property.value as string
            };
        case 'CustomPropertyFeedDto':
            return {
                $type: 'CustomPropertyFeedOverrideDto',
                value: property.value as FeedDto,
                l_VpId: property.l_VpId,
                l_VpName: property.name
            };
        case 'CustomPropertyFontDto':
            return {
                $type: 'CustomPropertyFontOverrideDto',
                value: property.value as Required<WidgetFontStyleDto>
            };
        case 'CustomPropertyImageDto':
            return {
                $type: 'CustomPropertyImageOverrideDto',
                value: property.value as IWidgetImage
            };
        case 'CustomPropertyNumberDto':
            return {
                $type: 'CustomPropertyNumberOverrideDto',
                value: property.value as number
            };
        case 'CustomPropertySelectDto':
            return {
                $type: 'CustomPropertySelectOverrideDto',
                value: property.value as WidgetSelectionOptionDto[]
            };
        case 'CustomPropertyTextDto':
            return {
                $type: 'CustomPropertyTextOverrideDto',
                value: property.value as string
            };
    }
}

export function convertCustomPropertiesOverrideToDapiDto(
    properties: WidgetElementDto['customProperties'],
    overrideProperties: WidgetElementOverrideDto['customProperties']
): WidgetElementDto['customProperties'] {
    const customProperties: WidgetElementDto['customProperties'] = [];

    for (const property of properties) {
        const propertyOverride = overrideProperties?.[property.name];
        if (propertyOverride) {
            customProperties.push(
                convertCustomPropertyOverrideTypeToPoolType(property, propertyOverride)
            );
        } else {
            customProperties.push(property);
        }
    }

    return customProperties;
}

function convertCustomPropertyOverrideTypeToPoolType(
    property: OneOfCustomPropertyDtos,
    propertyOverride: OneOfCustomPropertyOverrideDtos
): OneOfCustomPropertyDtos {
    switch (propertyOverride.$type) {
        case 'CustomPropertyBooleanOverrideDto':
            return {
                ...property,
                $type: 'CustomPropertyBooleanDto',
                value: propertyOverride.value
            };

        case 'CustomPropertyColorOverrideDto':
            return {
                ...property,
                $type: 'CustomPropertyColorDto',
                value: propertyOverride.value
            };

        case 'CustomPropertyFeedOverrideDto':
            return {
                ...property,
                $type: 'CustomPropertyFeedDto',
                value: propertyOverride.value
            };

        case 'CustomPropertyFontOverrideDto':
            return {
                ...property,
                $type: 'CustomPropertyFontDto',
                value: propertyOverride.value
            };

        case 'CustomPropertyImageOverrideDto':
            return {
                ...property,
                $type: 'CustomPropertyImageDto',
                value: propertyOverride.value
            };

        case 'CustomPropertyNumberOverrideDto':
            return {
                ...property,
                $type: 'CustomPropertyNumberDto',
                value: propertyOverride.value
            };
        case 'CustomPropertySelectOverrideDto':
            return {
                ...property,
                $type: 'CustomPropertySelectDto',
                value: propertyOverride.value
            };

        case 'CustomPropertyTextOverrideDto':
            return {
                ...property,
                $type: 'CustomPropertyTextDto',
                value: propertyOverride.value
            };
    }
}

export function convertCustomPropertiesToDapiDto(
    properties: IWidgetCustomProperty[]
): WidgetElementDto['customProperties'] {
    const customProperties: WidgetElementDto['customProperties'] = [];

    for (const property of properties) {
        customProperties.push(convertCustomPropertyToDapiDto(property));
    }

    return customProperties;
}

export function convertCustomPropertyToDapiDto(
    property: IWidgetCustomProperty
): OneOfCustomPropertyDtos {
    switch (property.unit) {
        case 'boolean':
            return {
                $type: 'CustomPropertyBooleanDto',
                value: property.value as boolean,
                label: property.label!,
                name: property.name
            };
        case 'color':
            return {
                $type: 'CustomPropertyColorDto',
                value: toRGBA(property.value as IColor),
                label: property.label!,
                name: property.name
            };
        case 'feed':
            return {
                $type: 'CustomPropertyFeedDto',
                value: isFeedValue(property.value) ? property.value : createFeed(''),
                l_VpId: property.versionPropertyId,
                l_VpName: property.name,
                label: property.label!,
                name: property.name
            };
        case 'font':
            return {
                $type: 'CustomPropertyFontDto',
                value: property.value as IFontStyle,
                label: property.label!,
                name: property.name
            };
        case 'image':
            return {
                $type: 'CustomPropertyImageDto',
                value: property.value as IWidgetImage,
                label: property.label!,
                name: property.name
            };
        case 'number':
            return {
                $type: 'CustomPropertyNumberDto',
                value: property.value as number,
                label: property.label!,
                name: property.name
            };
        case 'select':
            return {
                $type: 'CustomPropertySelectDto',
                value: property.value as IWidgetSelectOption[],
                label: property.label!,
                name: property.name
            };
        case 'text':
            return {
                $type: 'CustomPropertyTextDto',
                value: (property.value as IWidgetText).text,
                label: property.label!,
                l_VpId: property.versionPropertyId,
                l_VpName: property.name,
                name: property.name
            };
    }
}
